import { useEffect, useState } from 'react'
import { X, Check, CircleCheck, Loader2 } from 'lucide-react'
import { useFitThesisChat } from '@/hooks/useFitThesisChat'
import { fitThesisApi } from '@/lib/api/client'
import type { FitThesis, FitThesisBlockKey } from '@ispot/shared'
import { FitThesisChat } from './FitThesisChat'
import { FitThesisBlockPanel, type LabelResolver } from './FitThesisBlockPanel'
import type { PersonaSummary } from './FitThesisMatrix'

interface Props {
  thesis: FitThesis
  personas: Map<string, PersonaSummary>
  accountId: string
  projectId: string
  onClose(): void
  onBlockPatched(blockKey: FitThesisBlockKey, value: unknown): Promise<void>
  onValidate(): Promise<void>
}

export function FitThesisDrawer({ thesis, personas, accountId, projectId, onClose, onBlockPatched, onValidate }: Props) {
  const persona = personas.get(thesis.personaSessionId)
  const jtbdLabel = persona?.jtbds.find(j => j.id === thesis.jtbdId)?.label ?? thesis.jtbdId

  const chat = useFitThesisChat(accountId, projectId, thesis.id)

  // Local optimistic copy of the thesis, patched in response to block-saved events.
  const [localThesis, setLocalThesis] = useState<FitThesis>(thesis)
  useEffect(() => { setLocalThesis(thesis) }, [thesis])

  // Fetch label lookups once so block panel can render names, not raw UUIDs.
  const [labelMaps, setLabelMaps] = useState<{
    competitor: Map<string, string>
    flaw: Map<string, string>
    capability: Map<string, string>
  }>({ competitor: new Map(), flaw: new Map(), capability: new Map() })

  useEffect(() => {
    let cancelled = false
    fitThesisApi.listReferenceLabels(accountId, projectId)
      .then(({ competitors, flaws, capabilities }) => {
        if (cancelled) return
        setLabelMaps({
          competitor: new Map(competitors.map(r => [r.id, r.label])),
          flaw: new Map(flaws.map(r => [r.id, r.label])),
          capability: new Map(capabilities.map(r => [r.id, r.label])),
        })
      })
      .catch(() => { /* silent: block panel falls back to "Saved" placeholder */ })
    return () => { cancelled = true }
  }, [accountId, projectId])

  const resolveLabel: LabelResolver = (kind, id) => {
    if (!id) return null
    if (kind === 'competitor') return labelMaps.competitor.get(id) ?? null
    if (kind === 'flaw') return labelMaps.flaw.get(id) ?? null
    if (kind === 'capability') return labelMaps.capability.get(id) ?? null
    return null
  }

  useEffect(() => {
    if (!chat.lastBlockSaved) return
    // Merge the coach's tool-call into local state so block mini cards flip immediately.
    setLocalThesis(prev => applyBlock(prev, chat.lastBlockSaved!.blockKey, chat.lastBlockSaved!.value))
  }, [chat.lastBlockSaved])

  // Lock body scroll while drawer open
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Escape to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const canCommit = localThesis.blockCount === 9 && chat.complete
  const isValidated = localThesis.status === 'validated'

  const [committing, setCommitting] = useState(false)
  const [commitError, setCommitError] = useState<string | null>(null)

  async function handleCommit() {
    if (!canCommit || committing || isValidated) return
    setCommitting(true)
    setCommitError(null)
    try {
      await onValidate()
      setLocalThesis(t => ({ ...t, status: 'validated' }))
    } catch (err) {
      setCommitError(err instanceof Error ? err.message : 'Could not validate thesis')
    } finally {
      setCommitting(false)
    }
  }

  const statusChip = isValidated
    ? { label: 'Validated 9/9', bg: 'var(--color-success-bg)', fg: 'var(--color-success-fg)' }
    : localThesis.status === 'complete'
    ? { label: 'Complete 9/9', bg: 'var(--color-success-bg)', fg: 'var(--color-success-fg)' }
    : { label: `Draft ${localThesis.blockCount}/9`, bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-fg)' }

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-[rgba(42,36,30,0.32)]"
      onClick={onClose}
    >
      <div
        className="flex h-full w-[min(1040px,calc(100vw-80px))] flex-col border-l border-border bg-bg-card shadow-[-12px_0_40px_rgba(42,36,30,0.14)] max-[720px]:w-full"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex min-h-[92px] items-start justify-between gap-6 border-b border-border px-7 py-5 max-sm:flex-col max-sm:px-5">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-caption font-semibold uppercase tracking-wider text-fg-muted">
              FIT THESIS
              <span className="rounded-full px-2.5 py-0.5" style={{ background: statusChip.bg, color: statusChip.fg }}>
                {statusChip.label}
              </span>
              <span className="inline-flex items-center gap-1 text-fg-muted normal-case tracking-normal font-normal">
                <CircleCheck className="h-3 w-3" /> Autosaved
              </span>
            </div>
            <h2 className="font-display text-heading font-semibold leading-snug text-fg">
              {persona?.name ?? 'Persona'} x {jtbdLabel}
            </h2>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {isValidated ? (
              <span className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium" style={{ background: 'var(--color-success-bg)', color: 'var(--color-success-fg)' }}>
                <Check className="h-3.5 w-3.5" /> Validated
              </span>
            ) : (
              <button
                onClick={handleCommit}
                disabled={!canCommit || committing}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium text-bg-card transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: canCommit ? 'var(--color-accent)' : 'var(--color-border-strong)' }}
                title={canCommit ? 'Validate: lock in this thesis as a committed position' : 'Fill all 9 blocks first'}
              >
                {committing
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Validating</>
                  : <><Check className="h-3.5 w-3.5" /> Validate{canCommit ? '' : ' (need 9/9)'}</>}
              </button>
            )}
            <button onClick={onClose} className="rounded-full bg-bg-secondary p-2 text-fg-muted transition hover:bg-border" aria-label="Close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>

        {commitError && (
          <div className="border-b border-border px-7 py-2 text-caption" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger-fg)' }}>
            {commitError}
          </div>
        )}

        {isValidated && (
          <div className="flex items-center justify-between gap-3 border-b border-border px-7 py-3 text-body" style={{ background: 'var(--color-success-bg)', color: 'var(--color-success-fg)' }}>
            <span className="inline-flex items-center gap-2">
              <CircleCheck className="h-4 w-4" />
              Thesis validated. It will appear in the Gap Report and anchor your Solution Workspace.
            </span>
          </div>
        )}

        <div className="grid flex-1 grid-cols-[minmax(0,1fr)_360px] overflow-hidden max-[900px]:grid-cols-1">
          <FitThesisChat
            chat={chat}
            thesis={localThesis}
            personaName={persona?.name ?? null}
            jtbdLabel={jtbdLabel}
          />
          <FitThesisBlockPanel
            thesis={localThesis}
            persona={persona ?? null}
            lastSavedBlock={chat.lastBlockSaved?.blockKey ?? null}
            resolveLabel={resolveLabel}
            onDirectEdit={async (blockKey, value) => {
              const updated = applyBlock(localThesis, blockKey, value)
              setLocalThesis(updated)
              await onBlockPatched(blockKey, value)
            }}
          />
        </div>
      </div>
    </div>
  )
}

function applyBlock(t: FitThesis, key: FitThesisBlockKey, value: unknown): FitThesis {
  const patch: Partial<FitThesis> = {}
  switch (key) {
    case 'persona': patch.personaSessionId = String(value); break
    case 'jtbd': patch.jtbdId = String(value); break
    case 'pain': patch.painId = value ? String(value) : null; break
    case 'competitor': patch.competitorId = value ? String(value) : null; break
    case 'flaw': patch.competitorFlawId = value ? String(value) : null; break
    case 'consequence': {
      if (typeof value === 'string') { patch.consequenceText = value }
      else if (value && typeof value === 'object') {
        const v = value as { text?: string; severity?: string; denomination?: string }
        patch.consequenceText = v.text ?? null
        patch.consequenceSeverity = (v.severity ?? null) as FitThesis['consequenceSeverity']
        patch.consequenceDenomination = (v.denomination ?? null) as FitThesis['consequenceDenomination']
      }
      break
    }
    case 'pitch': patch.solutionPitch = value ? String(value) : null; break
    case 'benefitA': patch.benefitACapabilityId = value ? String(value) : null; break
    case 'benefitB': patch.benefitBCapabilityId = value ? String(value) : null; break
  }
  const merged = { ...t, ...patch } as FitThesis
  // Recompute block count locally for immediate feedback
  const filled: boolean[] = [
    !!merged.personaSessionId, !!merged.jtbdId, !!merged.painId,
    !!merged.competitorId, !!merged.competitorFlawId, !!merged.consequenceText,
    !!merged.solutionPitch, !!merged.benefitACapabilityId, !!merged.benefitBCapabilityId,
  ]
  merged.blockCount = filled.filter(Boolean).length
  merged.status = merged.blockCount === 0 ? 'empty' : merged.blockCount === 9 ? 'complete' : 'draft'
  return merged
}
